import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, RefreshCw, Repeat } from "lucide-react";
import { toast } from "sonner";

const blank = () => ({ label: "", category: "Salaire", amount: 0, day_of_month: 1, active: true, note: "" });

export default function FixedExpensesDialog({ open, onOpenChange, categories, onChanged }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/expenses/fixed").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { if (open) { load(); setForm(null); } }, [open]);

  const save = async () => {
    if (!form.label.trim()) { toast.error("Libellé requis"); return; }
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(form.amount) || 0, day_of_month: Number(form.day_of_month) || 1 };
      if (form.id) await api.put(`/expenses/fixed/${form.id}`, payload);
      else await api.post("/expenses/fixed", payload);
      toast.success("Dépense fixe enregistrée");
      setForm(null); load(); onChanged();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/expenses/fixed/${id}`); toast.success("Dépense fixe supprimée"); load(); };
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/expenses/fixed/generate");
      toast.success(data.created ? `${data.created} dépense(s) fixe(s) générée(s) pour ce mois` : "Toutes les dépenses fixes du mois sont déjà générées");
      onChanged();
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Repeat className="w-5 h-5 text-brand-600" /> Dépenses fixes mensuelles</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Chaque dépense fixe active est ajoutée automatiquement aux dépenses le jour choisi du mois, avec le statut « À payer ».</p>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="fixed-new-btn" size="sm" onClick={() => setForm(blank())} className="bg-brand-600 hover:bg-brand-700"><Plus className="w-4 h-4 mr-1.5" /> Nouvelle dépense fixe</Button>
          <Button data-testid="fixed-generate-btn" size="sm" variant="outline" onClick={generate} disabled={busy}><RefreshCw className="w-4 h-4 mr-1.5" /> Générer les dépenses du mois maintenant</Button>
        </div>

        {form && (
          <div data-testid="fixed-form" className="border rounded-xl p-4 space-y-3 bg-slate-50/60">
            <div><Label>Libellé *</Label><Input data-testid="fixed-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1" placeholder="Ex. Loyer local" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="fixed-category" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Montant ($)</Label><Input data-testid="fixed-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" /></div>
              <div><Label>Jour du mois (1-28)</Label><Input data-testid="fixed-day" type="number" min={1} max={28} value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Note</Label><Input value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1" /></div>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="fixed-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Active</label>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setForm(null)}>Annuler</Button>
              <Button data-testid="fixed-submit" size="sm" onClick={save} disabled={busy} className="bg-brand-600 hover:bg-brand-700">Enregistrer</Button>
            </div>
          </div>
        )}

        <div className="divide-y border rounded-xl">
          {items.map((f) => (
            <div key={f.id} data-testid={`fixed-row-${f.id}`} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
              <div className="min-w-0">
                <div className="font-medium truncate">{f.label} {!f.active && <span className="text-xs text-slate-400">(inactive)</span>}</div>
                <div className="text-xs text-slate-500">{f.category} · le {f.day_of_month} de chaque mois</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <b className="mr-2">{money(f.amount)}</b>
                <button data-testid={`fixed-edit-${f.id}`} onClick={() => setForm({ ...blank(), ...f })} className="p-1.5 text-slate-400 hover:text-brand-600"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`fixed-del-${f.id}`} onClick={() => del(f.id)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-center text-slate-400 text-sm py-8">Aucune dépense fixe</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
